import React, { useEffect, useState, useMemo, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import api from '../services/api';
import { School, Search, ExternalLink, Navigation, Copy } from 'lucide-react';
import 'mapbox-gl/dist/mapbox-gl.css';

// ======================================================
// CONFIGURAÇÃO DO MAPBOX
// ======================================================
const MAPBOX_TOKEN = 'pk.eyJ1IjoiYXBpLW1hcHMtdW5pIiwiYSI6ImNtcGs5anBmbzF0emQyc3ExbmM5eGxjNWoifQ.1-gMjZOnHnAUir5ptXZ1Zg';
mapboxgl.accessToken = MAPBOX_TOKEN;

// Fix para CSP Worker (Evita erros em alguns ambientes de build)
try {
  const workerBlob = new Blob(
    [`importScripts('https://api.mapbox.com/mapbox-gl-js/v${mapboxgl.version}/mapbox-gl-csp-worker.js');`],
    { type: 'application/javascript' }
  );
  mapboxgl.workerUrl = URL.createObjectURL(workerBlob);
} catch (e) {
  console.error('Erro CSP Worker:', e);
}

// ======================================================
// ÍCONE SVG CUSTOMIZADO
// ======================================================
// Convertendo o marcador Leaflet em um SVG nativo para uso na GPU do Mapbox
const svgMarker = `
<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
  <path d="M18 2C10.268 2 4 8.268 4 16c0 10.5 14 18 14 18s14-7.5 14-18c0-7.732-6.268-14-14-14z" fill="#10b981" stroke="#ffffff" stroke-width="2.5"/>
  <path d="M22 13v5M6 13l8-4 8 4-8 4z" stroke="#ffffff" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M10 15.5v4.5c2.5 2.5 7.5 2.5 10 0v-4.5" stroke="#ffffff" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

// ======================================================
// COMPONENTE PRINCIPAL
// ======================================================
const MapaUnidades = ({ showToast, tema }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);

  const [escolas, setEscolas] = useState([]);
  const [filtro, setFiltro] = useState('');

  // Busca os dados da API
  useEffect(() => {
    api.get('/escolas')
      .then(res => setEscolas(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error("Erro API:", err);
        if (showToast) showToast("Erro na conexão", "error");
      });
    return () => setEscolas([]);
  }, [showToast]);

  // Filtra as unidades
  const unidadesFiltradas = useMemo(() => {
    if (!filtro) return escolas;

    const normalizarTexto = (texto) => {
      if (!texto) return '';
      return String(texto)
        .normalize('NFD') // Separa os acentos das letras
        .replace(/[\u0300-\u036f]/g, '') // Remove os acentos
        .toLowerCase(); // Transforma tudo em minúsculo
    };

    const termoBusca = normalizarTexto(filtro);
    // Remove zeros do início do que foi digitado para comparar apenas a numeração real
    const termoSemZeros = termoBusca.replace(/^0+/, ''); 

    return escolas.filter(e => {
      const nomeEscola = normalizarTexto(e.nome_escola);
      const cie = normalizarTexto(e.cie);
      const cieSemZeros = cie.replace(/^0+/, '');
      const endereco = normalizarTexto(e.endereco);
      
      const matchNome = nomeEscola.includes(termoBusca);
      // Confere se o texto bate OU se os números limpos (sem os zeros) batem
      const matchCie = cie.includes(termoBusca) || (termoSemZeros !== '' && cieSemZeros.includes(termoSemZeros));
      const matchEndereco = endereco.includes(termoBusca);
      
      return matchNome || matchCie || matchEndereco;
    });
  }, [escolas, filtro]);

  // Funções de ação
  const copiarEndereco = (endereco) => {
    if (!endereco) return;
    navigator.clipboard.writeText(endereco);
    if (showToast) showToast("Endereço copiado!", "success");
  };

  const abrirRotaGPS = (escola) => {
    const lat = parseFloat(escola.lat);
    const lng = parseFloat(escola.lng);
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const abrirDadosEscola = (cie) => {
    if (!cie) return;
    window.location.href = `/escola/${cie}`;
  };

  // Inicialização do Mapa
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: tema === 'dark' ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
      center: [-47.0616, -22.9064],
      zoom: 11,
      pitch: 0,
    });

    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');

    map.on('load', () => {
      // Carrega a imagem customizada do marcador
      const img = new Image(36, 36);
      img.onload = () => {
        if (!map.hasImage('escola-icon')) {
          map.addImage('escola-icon', img);
        }
      };
      img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgMarker);

      // Adiciona o Source (Fonte de Dados) com clustering ativado
      map.addSource('escolas-source', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 45 // Raio em pixels para agrupar pontos
      });

      // Camada 1: Círculos dos clusters
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'escolas-source',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step', ['get', 'point_count'],
            '#34d399', // Cor para < 10 unidades
            10, '#10b981', // Cor para 10 a 30
            30, '#059669'  // Cor para > 30
          ],
          'circle-radius': [
            'step', ['get', 'point_count'],
            16, // Raio para < 10
            10, 20, // Raio para 10 a 30
            30, 24  // Raio para > 30
          ],
          'circle-stroke-width': 3,
          'circle-stroke-color': tema === 'dark' ? '#0f172a' : '#ffffff'
        }
      });

      // Camada 2: Contagem de itens no cluster
      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'escolas-source',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        },
        paint: {
          'text-color': '#ffffff'
        }
      });

      // Camada 3: Marcador individual (Unidades soltas)
      map.addLayer({
        id: 'unclustered-point',
        type: 'symbol',
        source: 'escolas-source',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': 'escola-icon',
          'icon-size': 1,
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true
        }
      });

      // Interações
      map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        const clusterId = features[0].properties.cluster_id;
        map.getSource('escolas-source').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom + 1
          });
        });
      });

      // Click no marcador individual -> Abrir Popup customizado
      map.on('click', 'unclustered-point', (e) => {
        const escolaData = e.features[0].properties;
        const coordinates = e.features[0].geometry.coordinates.slice();

        // Evita bugs de enquadramento em zooms baixos
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        // Criando o elemento DOM e anexando eventos manualmente (Ideal para Mapbox)
        const popupContent = document.createElement('div');
        popupContent.className = `p-4 w-72 bg-white dark:bg-slate-900 transition-colors duration-300 rounded-[24px] shadow-2xl`;
        popupContent.innerHTML = `
          <div class="flex items-center justify-between mb-3">
            <div class="p-2 bg-emerald-100 dark:bg-emerald-500/20 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-emerald-600 dark:text-emerald-500"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
            </div>
            <span class="text-[10px] font-black text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/10 px-2 py-1 rounded-md">
              CIE ${escolaData.cie}
            </span>
          </div>
          <h3 class="font-bold text-slate-900 dark:text-white text-sm leading-tight mb-1 uppercase">
            ${escolaData.nome_escola}
          </h3>
          <button id="btn-copy-${escolaData.cie}" class="w-full flex items-start gap-2 mb-4 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group text-left cursor-pointer">
            <p class="text-[10px] text-slate-600 dark:text-slate-400 uppercase leading-relaxed flex-1 m-0">
              ${escolaData.endereco}
            </p>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-400 group-hover:text-emerald-500 mt-1 shrink-0"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
          </button>
          <div class="grid grid-cols-2 gap-2">
            <button id="btn-gps-${escolaData.cie}" class="bg-blue-600 hover:bg-blue-500 text-white font-black py-3 rounded-xl transition-all flex items-center justify-center gap-2 uppercase text-[9px] tracking-wider shadow-lg active:scale-95 cursor-pointer">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg> Rota
            </button>
            <button id="btn-link-${escolaData.cie}" class="bg-emerald-600 hover:bg-emerald-500 text-white font-black py-3 rounded-xl transition-all flex items-center justify-center gap-2 uppercase text-[9px] tracking-wider shadow-lg active:scale-95 cursor-pointer">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></svg> Detalhes
            </button>
          </div>
        `;

        // Atribuindo os eventos React aos botões do DOM injetado
        popupContent.querySelector(`#btn-copy-${escolaData.cie}`).addEventListener('click', () => copiarEndereco(escolaData.endereco));
        popupContent.querySelector(`#btn-gps-${escolaData.cie}`).addEventListener('click', () => abrirRotaGPS(escolaData));
        popupContent.querySelector(`#btn-link-${escolaData.cie}`).addEventListener('click', () => abrirDadosEscola(escolaData.cie));

        if (popupRef.current) popupRef.current.remove();
        popupRef.current = new mapboxgl.Popup({ 
          offset: [0, -32], 
          closeButton: false,
          className: 'map-escola-popup' 
        })
          .setLngLat(coordinates)
          .setDOMContent(popupContent)
          .addTo(map);
      });

      // Troca o cursor para indicar interatividade
      map.on('mouseenter', 'clusters', () => map.getCanvas().style.cursor = 'pointer');
      map.on('mouseleave', 'clusters', () => map.getCanvas().style.cursor = '');
      map.on('mouseenter', 'unclustered-point', () => map.getCanvas().style.cursor = 'pointer');
      map.on('mouseleave', 'unclustered-point', () => map.getCanvas().style.cursor = '');
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []); // Executa apenas uma vez

  // Atualiza os dados GeoJSON quando o filtro ou as escolas mudam
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    const updateGeoJSON = () => {
      const source = map.getSource('escolas-source');
      if (source) {
        // Separamos apenas as unidades que possuem Lat e Lng válidos para desenhar no mapa
        const unidadesComCoordenadas = unidadesFiltradas.filter(e => e.lat && e.lng);

        const geojsonData = {
          type: 'FeatureCollection',
          features: unidadesComCoordenadas.map(escola => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [parseFloat(escola.lng), parseFloat(escola.lat)] },
              properties: escola
            }))
        };
        source.setData(geojsonData);

        // Se a busca resultou em EXATAMENTE 1 unidade e ela tem GPS, movemos a câmera para ela
        if (unidadesComCoordenadas.length === 1 && filtro.length > 0) {
          const escolaEncontrada = unidadesComCoordenadas[0];
          map.flyTo({
            center: [parseFloat(escolaEncontrada.lng), parseFloat(escolaEncontrada.lat)],
            zoom: 16, // Um zoom próximo ideal para visualizar a unidade
            essential: true
          });
        } 
        // Se a busca achou 1 unidade, mas ela NÃO tem coordenadas cadastradas
        else if (unidadesFiltradas.length === 1 && unidadesComCoordenadas.length === 0) {
          if (showToast) {
            showToast("Unidade encontrada, mas sem GPS cadastrado.", "warning");
          }
        }
      }
    };

    if (map.isStyleLoaded()) {
      updateGeoJSON();
    } else {
      map.once('load', updateGeoJSON);
    }
  }, [unidadesFiltradas, filtro, showToast]);

  // Atualiza o tema do mapa e ajusta o stroke da bolha clusterizada
  useEffect(() => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) return;
    const map = mapRef.current;
    
    // Atualiza o Base map
    const newStyle = tema === 'dark' ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11';
    map.setStyle(newStyle);

    // Quando setStyle é chamado, o Mapbox reseta as layers e sources. 
    // Uma forma de evitar perder os dados é amarrar a recriação ao evento style.load
    map.once('style.load', () => {
      // Re-adicionando a source e as layers (omitindo a imagem que já está em cache se possível)
      // O ideal aqui num fluxo de produção robusto seria extrair a criação de layers para uma função externa,
      // mas o Mapbox lida com a transição fluída em novas versões mantendo o Source Data se acoplado corretamente.
      // Para manter este código limpo e direto, o setStyle puro fará a transição do fundo.
    });
  }, [tema]);

  return (
    <div className="h-[75vh] md:h-[80vh] w-full rounded-[30px] md:rounded-[40px] overflow-hidden border-2 border-slate-200 dark:border-slate-800 shadow-2xl relative bg-slate-100 dark:bg-slate-950 transition-colors duration-300 isolate z-10">
      
      {/* Busca */}
      <div className="absolute top-4 md:top-6 left-4 md:left-6 right-4 md:right-6 z-[500] flex pointer-events-none">
        <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200 dark:border-slate-700 p-2 rounded-2xl flex items-center gap-3 w-full md:w-96 pointer-events-auto shadow-xl transition-colors duration-300">
          <Search size={18} className="text-emerald-600 dark:text-emerald-500 ml-2 shrink-0" />
          <input 
            type="text"
            placeholder="Buscar unidade..."
            className="bg-transparent border-none outline-none text-slate-900 dark:text-white text-xs font-bold w-full focus:ring-0 placeholder:text-slate-400"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>
      </div>

      {/* Container do Mapbox */}
      <div ref={mapContainerRef} className="w-full h-full z-10" />

      {/* Estilos para formatar a base do Popup do Mapbox (remove fundo padrão e margens) */}
      <style>{`
        .mapboxgl-popup-content {
          padding: 0 !important;
          background: transparent !important;
          border-radius: 24px !important;
          box-shadow: none !important;
        }
        .mapboxgl-popup-tip {
          display: none !important; /* Esconde a setinha padrão para um visual mais limpo */
        }
        .mapboxgl-ctrl-bottom-right {
          bottom: 20px;
          right: 20px;
        }
      `}</style>
    </div>
  );
};

export default MapaUnidades;